import { useState } from "react";
import { useSearchParams } from "react-router";
import { Plus, Target } from "lucide-react";
import { InitiativesStats } from "@/components/initiatives/initiatives-stats";
import { InitiativesCharts } from "@/components/initiatives/initiatives-charts";
import { InitiativesTable } from "@/components/initiatives/initiatives-table";
import { CreateInitiativeWizard } from "@/components/initiatives/create-initiative-wizard";
import { FundInitiativeModal } from "@/components/initiatives/fund-initiative-modal";
import { InviteParticipantModal } from "@/components/initiatives/invite-participant-modal";
import { ReactivateInitiativeModal } from "@/components/initiatives/reactivate-initiative-modal";
import { OpenInitiativesTab } from "@/components/initiatives/open-initiatives-tab";
import { ParticipatedTab } from "@/components/initiatives/participated-tab";
import { JoinInitiativeModal } from "@/components/initiatives/join-initiative-modal";
import { useAppContext } from "@/providers";
import { useInitiatives } from "@/hooks/use-initiatives";
import { useOpenInitiatives } from "@/hooks/use-open-initiatives";
import { useInitiativeStats } from "@/hooks/use-initiative-stats";
import { useWalletBalance } from "@/hooks/use-wallet-balance";
import { parseApiFloat } from "@/lib/utils/format";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import type { Initiative } from "@/types/initiative";

type ActiveModal = "fund" | "invite" | "reactivate" | null;
type TabKey = "mine" | "open" | "participated";

interface TabDef {
  key: TabKey;
  ar: string;
  en: string;
}

const TABS: TabDef[] = [
  { key: "mine", ar: "مبادراتي", en: "My initiatives" },
  { key: "open", ar: "مبادرات مفتوحة", en: "Open" },
  { key: "participated", ar: "شاركتُ بها", en: "Participated" },
];

export default function InitiativesPage() {
  const { locale, theme } = useAppContext();
  const isDark = theme === "dark";
  const isRTL = locale === "ar";

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as TabKey) ?? "mine";

  const [showWizard, setShowWizard] = useState(false);
  const [tableRefetchTrigger, setTableRefetchTrigger] = useState(0);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [selectedInitiative, setSelectedInitiative] =
    useState<Initiative | null>(null);
  const [joinInitiative, setJoinInitiative] = useState<Initiative | null>(null);

  const { refetch } = useInitiatives({ initialPerPage: 10 });
  const {
    openInitiatives,
    participatedInitiatives,
    openLoading,
    participatedLoading,
    error: openError,
    refetchOpen,
    refetchParticipated,
  } = useOpenInitiatives(activeTab !== "mine");
  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
    refetch: statsRefetch,
  } = useInitiativeStats();
  const { data: walletData } = useWalletBalance();

  const handleTabChange = (tab: TabKey) => {
    setSearchParams({ tab });
  };

  const handleAction = (action: ActiveModal, initiative: Initiative) => {
    setSelectedInitiative(initiative);
    setActiveModal(action);
  };

  const handleModalSuccess = () => {
    setActiveModal(null);
    setSelectedInitiative(null);
    refetch();
  };

  const handleModalClose = () => {
    setActiveModal(null);
    setSelectedInitiative(null);
  };

  return (
    <>
      {/* ─── Editorial hero ──────────────────────────────────────────── */}
      <section className="w-full border-b border-border bg-card relative overflow-hidden">
        {/*<div
          aria-hidden
          className="absolute -top-24 -end-24 w-72 h-72 rounded-full bg-primary/5 pointer-events-none"
        />
        <div
          aria-hidden
          className="absolute -bottom-24 -start-12 w-56 h-56 rounded-full bg-secondary/5 pointer-events-none"
        />*/}
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10 space-y-5">
         
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="space-y-2 max-w-2xl">
              <h1 className="font-display font-bold text-3xl md:text-4xl text-foreground tracking-tight leading-tight">
                {isRTL ? "أطلِق مبادرات ذات أثر" : "Run impactful initiatives"}
              </h1>
              <p className="text-[14px] text-foreground-secondary leading-relaxed">
                {isRTL
                  ? "تتبّع مبادراتك، ادعَ المشاركين، وقِس الأثر في مكان واحد."
                  : "Track your initiatives, invite participants, and measure impact in one place."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowWizard(true)}
              className={cn(
                "inline-flex items-center gap-2 h-11 px-6 rounded-full border transition-all active:scale-[0.97]",
                "text-[13px] font-bold shadow-sm shrink-0",
                "bg-primary text-white border-primary hover:bg-primary-dark",
              )}
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              <span>{t(locale, "createInitiative")}</span>
            </button>
          </div>

          <InitiativesStats
            locale={locale}
            data={statsData}
            loading={statsLoading}
            error={statsError}
            refetch={statsRefetch}
          />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-12 space-y-10 md:space-y-12">
        {/* ─── Tabs ────────────────────────────────────────────────── */}
        <div
          role="tablist"
          aria-label={t(locale, "initiatives")}
          className="flex border-b border-border"
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabChange(tab.key)}
                className={cn(
                  "inline-flex items-center px-4 h-10 text-[13px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-foreground-secondary hover:text-foreground hover:border-border",
                )}
              >
                {isRTL ? tab.ar : tab.en}
              </button>
            );
          })}
        </div>

        {/* ─── My Initiatives tab ──────────────────────────────────── */}
        {activeTab === "mine" && (
          <div className="space-y-10 md:space-y-12">
            <InitiativesCharts
              locale={locale}
              isDark={isDark}
              data={statsData}
              loading={statsLoading}
              error={statsError}
              refetch={statsRefetch}
            />
            <InitiativesTable locale={locale} onAction={handleAction} refetchTrigger={tableRefetchTrigger} />
          </div>
        )}

        {/* ─── Open Initiatives tab ────────────────────────────────── */}
        {activeTab === "open" && (
          <OpenInitiativesTab
            initiatives={openInitiatives}
            loading={openLoading}
            error={openError}
            locale={locale}
            onJoin={(initiative) => setJoinInitiative(initiative)}
          />
        )}

        {/* ─── Participated tab ────────────────────────────────────── */}
        {activeTab === "participated" && (
          <ParticipatedTab
            initiatives={participatedInitiatives}
            loading={participatedLoading}
            error={openError}
            locale={locale}
          />
        )}

        {/* ─── Empty hint when no data on mine tab ─────────────────── 
        {activeTab === "mine" &&
          !statsLoading &&
          !statsError &&
          statsData &&
          statsData.total_initiatives === 0 && (
            <EmptyHint
              isRTL={isRTL}
              onCreate={() => setShowWizard(true)}
              createLabel={t(locale, "createInitiative")}
            />
          )}*/}
      </div>

      <CreateInitiativeWizard
        open={showWizard}
        locale={locale}
        onClose={() => setShowWizard(false)}
        onSuccess={() => {
          setShowWizard(false);
          setTableRefetchTrigger((n) => n + 1);
        }}
      />

      {activeModal === "fund" && selectedInitiative && (
        <FundInitiativeModal
          initiative={selectedInitiative}
          walletBalance={parseApiFloat(walletData?.n_c_balance ?? "0")}
          locale={locale}
          onSuccess={handleModalSuccess}
          onClose={handleModalClose}
        />
      )}

      {activeModal === "invite" && selectedInitiative && (
        <InviteParticipantModal
          initiativeId={selectedInitiative.id}
          locale={locale}
          onSuccess={handleModalSuccess}
          onClose={handleModalClose}
        />
      )}

      {activeModal === "reactivate" && selectedInitiative && (
        <ReactivateInitiativeModal
          initiative={selectedInitiative}
          locale={locale}
          onSuccess={handleModalSuccess}
          onClose={handleModalClose}
        />
      )}

      {joinInitiative && (
        <JoinInitiativeModal
          initiative={joinInitiative}
          locale={locale}
          onClose={() => setJoinInitiative(null)}
          onSuccess={() => {
            setJoinInitiative(null);
            void refetchOpen();
            void refetchParticipated();
          }}
        />
      )}
    </>
  );
}

function EmptyHint({
  isRTL,
  onCreate,
  createLabel,
}: {
  isRTL: boolean;
  onCreate: () => void;
  createLabel: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-white p-10 md:p-12 text-center space-y-5">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary">
        <Target className="w-6 h-6" strokeWidth={1.8} />
      </div>
      <div className="space-y-2">
        <h3 className="font-display font-bold text-xl md:text-2xl text-foreground">
          {isRTL ? "لا مبادرات بعد" : "No initiatives yet"}
        </h3>
        <p className="text-[14px] text-foreground-secondary max-w-md mx-auto">
          {isRTL
            ? "ابدأ بإطلاق أوّل مبادرة لك — نُجهّز كل شيء."
            : "Launch your first initiative — we'll set everything up."}
        </p>
      </div>
      <button
        type="button"
        onClick={onCreate}
        className={cn(
          "inline-flex items-center gap-2 h-11 px-6 rounded-full border transition-all active:scale-[0.97]",
          "text-[13px] font-bold shadow-sm",
          "bg-primary text-white border-primary hover:bg-primary-dark",
        )}
      >
        <Plus className="w-4 h-4" strokeWidth={2} />
        <span>{createLabel}</span>
      </button>
    </div>
  );
}
